// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/Sidebar'

import { EXPORT_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'

// components
import SelectOverlay from '@aytm/app/stats/page/sidebar/components/SelectOverlay'

import ResponseLevelData from '@aytm/app/stats/page/sidebar/export/components/ResponseLevelData'
import Questionnaire from '@aytm/app/stats/page/sidebar/export/components/Questionnaire'
import Specific from '@aytm/app/stats/page/sidebar/export/components/Specific'
import Summary from '@aytm/app/stats/page/sidebar/export/components/Summary'
import Crosstab from '@aytm/app/stats/page/sidebar/export/components/Crosstab'
import Presentation from '@aytm/app/stats/page/sidebar/export/components/Presentation'
import Attachments from '@aytm/app/stats/page/sidebar/export/components/Attachments'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import SidebarPopupWarning from '@aytm/app/stats/page/sidebar/components/SidebarPopupWarning'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// styles
import style from '@aytm/app/stats/page/sidebar/export/styles/ExportPanel.module.scss'

// Survey draft in *.doc fileType='draft' expotType='draft-doc'
// Survey draft with comments fileType='draft' expotType='draft-doc-comments'
// Raw CSV fileType='csv' expotType='raw'
// Excel raw fileType='excel' expotType='raw'
// Excel summary fileType='excel' expotType='summary'
// Excel with coded map fileType='excel_numbers' expotType='mapped'
// SPSS fileType='spss' expotType='mapped'
// JMP fileType='jmp' expotType='mapped'

// Research Analyses categories: `key` doubles as the dropdown key and the `model.export.<key>` source array.
// `gate` picks the disable predicate (`specific` vs `authorized`); `feature` is an extra per-type feature gate.
const RESEARCH_ANALYSES = [
  { key: 'conjoint', text: 'Conjoint Analyses', icon: 'cjd', gate: 'specific' },
  { key: 'emd', text: 'Impression Dial Analyses', icon: 'emd', gate: 'authorized' },
  { key: 'ec', text: 'ECommerce Events', icon: 'ec', gate: 'authorized', feature: 'ec_export' },
  { key: 'concept_lab', text: 'Concept Lab', icon: 'dld', gate: 'authorized' },
  { key: 'concept_mapping', text: 'Concept Mapping', icon: 'dld-mapping', gate: 'authorized' },
  { key: 'tracker_lab', text: 'Tracker Lab', icon: 'dlt', gate: 'authorized' },
  { key: 'tracker_mapping', text: 'Tracker Mapping', icon: 'dlt-mapping', gate: 'authorized' }
]

const ExportPanel = ({
  survey,
  model,
  report,
  features,
  filteredQuestions,
  onExport,
  onModeChange,
  onParamsChange,
  onAttachmentUpload,
  onAttachmentRemove
}) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  const { isReportVirtual, isReportXpert } = model

  const isSpecial = 'charts' != model.mode

  const specificExportDisabled = !PageGlobals.USER_ID || isSpecial || features.export.disabled || false
  const specificAuthorizedDisabled =
    specificExportDisabled || features.authorized_exports.disabled || isReportVirtual || isReportXpert || false

  const [failed, setFailed] = useState(sidebarState.export.failed || null)

  const [processing, setProcessing] = useState(sidebarState.export.processing || null)

  const [exportType, setExportType] = useState(sidebarState.export.exportType || null)

  // Per-sub-panel disable predicates, computed here and passed down as the `disabled` prop.
  const specificDisabled = ('turf' == exportType && features.turf.disabled) || features.export.disabled || false
  const responseDisabled = features.export.disabled || false
  const summaryDisabled = report.current.scripts?.info?.disable_excel_summary || features.export.disabled || false
  const presentationDisabled =
    isSpecial ||
    !PageGlobals.USER_ID ||
    (PageGlobals.SHARE_KEY != undefined && PageGlobals.SHARE_KEY.key != undefined) ||
    report.current.scripts?.info?.disable_pptx ||
    features.export.disabled ||
    false

  const componentRef = useRef(null)

  const exportTypeRef = useRef(null)
  exportTypeRef.current = exportType

  const [root, setRoot] = useState(null)

  const setComponentRef = useCallback(node => {
    componentRef.current = node
    setRoot(node?.parentNode ?? null)
  }, [])

  const isOeExists = useMemo(
    () =>
      !isReportVirtual &&
      !isReportXpert &&
      (0 < filteredQuestions.length || survey.questions.some(question => 'ir' == question.type)),
    [survey.questions, filteredQuestions, isReportVirtual, isReportXpert]
  )

  const isTurfEnabled = useMemo(
    () => 0 < model.export.turf.length && !features.turf.hidden && !specificExportDisabled,
    [model.export.turf, features.turf.hidden, specificExportDisabled]
  )

  // The sidebar refreshes these lists inside the same export object, so the object's identity cannot
  // tell the options that a category just lost its last question.
  const researchAnalysesPresence = RESEARCH_ANALYSES.map(({ key }) => (0 < model.export[key].length ? 1 : 0)).join('')

  const exportTypeOptions = useMemo(() => {
    const options = []

    if (!features.correlation_scan.hidden)
      options.push({ text: 'Banner tables/Crosstabs', key: 'crosstab', icon: 'crosstab-v2' })

    options.push({ text: 'Response level data', key: 'response', icon: 'excel-v2' })
    options.push({ text: 'Summary data', key: 'summary', icon: 'excel-v2' })

    RESEARCH_ANALYSES.forEach(({ key, text, icon, gate, feature }) => {
      const gated = 'specific' == gate ? specificExportDisabled : specificAuthorizedDisabled
      if (0 < model.export[key].length && !gated && (!feature || !features[feature].disabled))
        options.push({ text, key, icon: PageGlobals.QUESTION_TYPE_ICONS[icon] })
    })

    isTurfEnabled && options.push({ text: 'TURF Analyses', key: 'turf', icon: 'turf-v2' })

    options.push({ text: 'Presentation', key: 'presentation', icon: 'export-powerpoint-v2' })

    if (!features.print_survey_draft.disabled)
      options.push({ text: 'Questionnaire', key: 'questionnaire', icon: 'questionnaire' })

    // can_create_attachment alone gates the upload path (master parity); the expert study is
    // auto-created server-side on first upload, so it must not require a pre-existing es_id.
    if (PageGlobals.CAN_CREATE_ATTACHMENT || (window.report_exists && PageGlobals.CAN_VIEW_ATTACHMENT))
      options.push({ text: 'Attachments', key: 'attachments', icon: 'attachments' })

    return options
  }, [
    model.export,
    researchAnalysesPresence,
    specificExportDisabled,
    specificAuthorizedDisabled,
    features.ec_export.disabled,
    features.correlation_scan.hidden,
    features.print_survey_draft.disabled,
    isTurfEnabled
  ])

  const itemTemplate = option => (
    <span className={option.icon ? 'p-item-label' : ''}>
      {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
      <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
    </span>
  )
  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon && <i className={`icon-aytmfd-${option.icon}`} />}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const onSelectExportType = value => {
    sidebarDispatch({
      type: 'UPDATE_STATE',
      key: 'export',
      state: { exportType: value }
    })
  }

  const onExportClick = useCallback(params => onExport(exportType, params), [exportType])

  useEffect(() => {
    setExportType(sidebarState.export.exportType || null)

    setFailed(sidebarState.export.failed || null)
    setProcessing(sidebarState.export.processing || null)
  }, [sidebarState.export])

  useEffect(() => {
    if (exportTypeOptions.some(item => item.key == exportTypeRef.current)) return

    setExportType(null)
  }, [exportTypeOptions])

  return (
    <>
      {'crosstab' == exportType ? (
        <SidebarPopupWarning
          features={[features['correlation_scan']]}
          isSpecial={isSpecial}
          isReportVirtual={isReportVirtual}
          isReportXpert={isReportXpert}
          onModeChange={onModeChange}
        />
      ) : 'response' == exportType ? (
        <SidebarPopupWarning
          message={
            0 < PageGlobals.USER_ID && 'off' == features.individual_data?.status?.raw
              ? "You'll need to ask your account owner to export individual data."
              : null
          }
          disabled={responseDisabled}
          onModeChange={onModeChange}
        />
      ) : 'summary' == exportType ? (
        <SidebarPopupWarning disabled={summaryDisabled} onModeChange={onModeChange} />
      ) : 'turf' == exportType ? (
        <SidebarPopupWarning
          features={[features['turf']]}
          isSpecial={isSpecial}
          isReportVirtual={isReportVirtual}
          isReportXpert={isReportXpert}
          onModeChange={onModeChange}
        />
      ) : 'presentation' == exportType ? (
        <SidebarPopupWarning isSpecial={isSpecial} disabled={presentationDisabled} onModeChange={onModeChange} />
      ) : 'questionnaire' == exportType ? (
        <SidebarPopupWarning isReportXpert={isReportXpert} disabled={isReportXpert} onModeChange={onModeChange} />
      ) : 'attachments' != exportType ? (
        <SidebarPopupWarning
          features={[features['export']]}
          isSpecial={isSpecial}
          isReportVirtual={isReportVirtual}
          isReportXpert={isReportXpert}
          onModeChange={onModeChange}
        />
      ) : null}

      <div ref={setComponentRef} className={style.component}>
        <div className={style['component-title']}>
          {isSpecial ? 'Export comments' : 'Export options'}
          {root && <SidebarPopup id="export" root={root} help={EXPORT_HELP} width="300px" />}
        </div>

        {exportType ? (
          <>
            <div className={style.row}>
              <div className="p-fluid p-col-12">
                <SelectOverlay
                  data-testid="stat-exports-selector"
                  className="p-dropdown-rounded p-dropdown-light-blue p-dropdown-no-scroll"
                  value={exportType}
                  options={exportTypeOptions}
                  itemTemplate={itemTemplate}
                  valueTemplate={valueTemplate}
                  onChange={onSelectExportType}
                />
              </div>
            </div>

            {(() => {
              switch (exportType) {
                case 'crosstab':
                  return (
                    <Crosstab
                      root={root}
                      survey={survey}
                      model={model}
                      report={report}
                      features={features}
                      processing={processing}
                      onExport={onExportClick}
                      onParamsChange={onParamsChange}
                    />
                  )
                case 'response':
                  return (
                    <ResponseLevelData
                      survey={survey}
                      model={model}
                      report={report}
                      features={features}
                      isOeExists={isOeExists}
                      processing={processing}
                      disabled={!report.current.schema.length || responseDisabled}
                      onExport={onExportClick}
                      onParamsChange={onParamsChange}
                    />
                  )
                case 'summary':
                  return (
                    <Summary
                      survey={survey}
                      model={model}
                      report={report}
                      features={features}
                      isOeExists={isOeExists}
                      processing={processing}
                      disabled={!report.current.schema.length || summaryDisabled}
                      onExport={onExportClick}
                      onParamsChange={onParamsChange}
                    />
                  )
                case 'turf':
                case 'conjoint':
                case 'emd':
                case 'ec':
                case 'concept_lab':
                case 'concept_mapping':
                case 'tracker_lab':
                case 'tracker_mapping':
                  return (
                    <Specific
                      root={root}
                      model={model}
                      data={model.export}
                      report={report}
                      features={features}
                      exportType={exportType}
                      processing={processing}
                      disabled={!report.current.schema.length || specificDisabled}
                      onExport={onExportClick}
                    />
                  )
                case 'presentation':
                  return (
                    <Presentation
                      survey={survey}
                      model={model}
                      report={report}
                      features={features}
                      filteredQuestions={filteredQuestions}
                      isOeExists={isOeExists}
                      processing={processing}
                      disabled={!report.current.schema.length || !model.totals.responses || presentationDisabled}
                      onExport={onExportClick}
                      onParamsChange={onParamsChange}
                    />
                  )
                case 'questionnaire':
                  return <Questionnaire processing={processing} disabled={isReportXpert} onExport={onExportClick} />
                case 'attachments':
                  return <Attachments features={features} onUpload={onAttachmentUpload} onRemove={onAttachmentRemove} />
                default:
                  return null
              }
            })()}
          </>
        ) : (
          <div className={style.buttons}>
            {exportTypeOptions.map(option => (
              <div key={option.key} className={`${style.row} p-fluid`}>
                <div
                  data-testid={`stat-exports-${option.key}`}
                  className="p-component p-button p-button-rounded p-button-light-blue"
                  {...pressable(() => onSelectExportType(option.key))}
                >
                  <i className={`icon-aytmfd-${option.icon}`} />
                  <span className={style['button-text']}>{option.text}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {null !== failed && failed.exportType == exportType ? (
          <div className={`${style.row} ${style['row-error']}`}>
            An error occurred while performing selected export operation. If the problem is reproducible please contact
            Support team.
          </div>
        ) : null}
      </div>
    </>
  )
}

ExportPanel.propTypes = {
  survey: PropTypes.object,
  model: PropTypes.object,
  report: PropTypes.object,
  features: PropTypes.object,
  filteredQuestions: PropTypes.array,
  onExport: PropTypes.func,
  onModeChange: PropTypes.func,
  onParamsChange: PropTypes.func,
  onAttachmentUpload: PropTypes.func,
  onAttachmentRemove: PropTypes.func
}

ExportPanel.displayName = 'ExportPanel'

export default ExportPanel
